import type { AuditLogEntry } from "@wallyo/contracts/console";
import type { ConsoleSubscription, ListProSubscriptionsResponse } from "@wallyo/contracts/console/billing";
import type { CustomerAccount, ResolvedCustomerSetting } from "@wallyo/contracts/console/accounts";
import { ACCOUNT_RECENT_JOBS_LIMIT } from "@wallyo/contracts/console/accounts";
import { Fragment, type FormEvent } from "react";
import { ConsoleApiError } from "../../api/errors";
import { describeError } from "../../auth/error-message";
import { categoryName, isolate, settingName, t, type MessageId } from "../../i18n";
import { formatTime } from "../../i18n/format";
import { ErrorNotice } from "../../shell/ErrorNotice";
import { PageTop } from "../../shell/PageTop";
import { auditHash } from "../audit-filters";
import type { BillingListState } from "../billing/billing-model";
import { describeActor, describeChange, shortId } from "../audit-format";
import type { AuditLogState } from "../audit-log-state";
import { Modal } from "../team/TeamDialogs";
import {
  approval,
  describeAccountKind,
  describeRestrictions,
  displayValue,
  formatDay,
  formatMonth,
  jobStatus,
  nameOf,
  otherParty,
  rate,
  rating,
  regionName,
  sourceLabel,
  subscription,
  tradeName,
  valueTone,
  verification,
} from "./customer-format";
import { CUSTOMER_TABS, customerHref, type CustomerTab } from "./customer-route";
import { SubscriptionView } from "./SubscriptionView";
import { FeatureLock } from "./FeatureLock";
import { REVEAL_REASON_MAX_LENGTH, type RevealEvent, type RevealState } from "./reveal-state";

export type CustomerPageViewProps = {
  account: CustomerAccount;
  tab: CustomerTab;
  reveal: RevealState;
  canReveal: boolean;
  canViewAudit: boolean;
  journal: AuditLogState;
  /** The Abonnement tab: read from billing when that tab opens (ADR-0018 §10). */
  subscriptions: {
    state: BillingListState<ListProSubscriptionsResponse>;
    canManage: boolean;
    canOfferDiscounts: boolean;
    onExtend: (subscription: ConsoleSubscription) => void;
    onEndNow: (subscription: ConsoleSubscription) => void;
    onRetry: () => void;
  };
  roles: readonly string[];
  onTab: (tab: CustomerTab) => void;
  onReveal: (event: RevealEvent) => void;
  onJournalRetry: () => void;
  onJournalMore: () => void;
  now?: Date;
};

const TAB_LABELS: Record<CustomerTab, MessageId> = {
  overview: "customers.tab.overview",
  subscription: "customers.tab.subscription",
  settings: "customers.tab.settings",
  jobs: "customers.tab.jobs",
  audit: "customers.tab.audit",
};

/** Only a pro has an abonnement: the tab isn't offered to anyone else. */
function tabsFor(account: CustomerAccount): readonly CustomerTab[] {
  if (account.proProfile) return CUSTOMER_TABS;
  return CUSTOMER_TABS.filter((tab) => tab !== "subscription");
}

/** A customer's page, as in the approved prototype: who they are, then Aperçu, réglages, demandes and journal. */
export function CustomerPageView(props: CustomerPageViewProps) {
  const { account, tab } = props;
  const { identity } = account;
  const deleted = identity.deletedAt !== null;
  const title = deleted ? t("customers.page.deletedTitle") : nameOf(identity);

  return (
    <>
      <PageTop title={title} sub={<HeaderSub {...props} />} />
      <div className="crumbs">
        <a href="#customer">{t("customers.back")}</a>
        <span className="meta mono" title={identity.userId}>
          {identity.userId}
        </span>
      </div>
      {props.reveal.status === "revealed" ? <RevealedDetails {...props} /> : null}
      {deleted ? null : <OnBehalfPanel name={identity.firstName ?? nameOf(identity)} />}
      <div className="tabs" role="tablist" aria-label={t("customers.tabs.aria")}>
        {tabsFor(account).map((id) => (
          <button
            key={id}
            className={`tab ${tab === id ? "on" : ""}`}
            role="tab"
            type="button"
            aria-selected={tab === id}
            onClick={() => props.onTab(id)}
          >
            {t(TAB_LABELS[id])}
          </button>
        ))}
      </div>
      <div role="tabpanel">
        {tab === "overview" ? <Overview account={account} now={props.now} /> : null}
        {tab === "subscription" ? <Subscription {...props} /> : null}
        {tab === "settings" ? <SettingsTable account={account} now={props.now} /> : null}
        {tab === "jobs" ? <Jobs account={account} now={props.now} /> : null}
        {tab === "audit" ? <Journal {...props} /> : null}
      </div>
      {props.reveal.status === "asking" || props.reveal.status === "sending" ? <RevealDialog {...props} /> : null}
    </>
  );
}

// ── Header ────────────────────────────────────────────────────────────────

function HeaderSub({ account, canReveal, reveal, onReveal }: CustomerPageViewProps) {
  const { identity } = account;
  if (identity.deletedAt !== null) {
    return (
      <>
        <span className="pill bad">{t("customers.deleted")}</span>{" "}
        {t("customers.page.deletedSub", { date: formatDay(identity.deletedAt) })}
      </>
    );
  }
  const hasContact = identity.maskedPhone !== null || identity.maskedEmail !== null;
  return (
    <span className="customer-sub">
      <span>
        {t("customers.page.sub", {
          kind: describeAccountKind(account),
          since: formatMonth(identity.createdAt),
          region: regionName(identity.region),
        })}
      </span>
      <span className="contact">
        <span className="mono" data-contact="masked">
          {identity.maskedPhone ?? "—"}
        </span>
        {identity.maskedEmail ? <span className="mono meta">{identity.maskedEmail}</span> : null}
        {!hasContact ? null : canReveal ? (
          reveal.status === "revealed" ? null : (
            <button className="btn small" type="button" onClick={() => onReveal({ type: "open" })}>
              {t("customers.reveal.button")}
            </button>
          )
        ) : (
          <span className="locked" title={t("customers.reveal.lockedTitle")}>
            {t("customers.reveal.locked")}
          </span>
        )}
      </span>
    </span>
  );
}

/** The details the server sent, for this page view only. */
function RevealedDetails({ reveal, onReveal }: CustomerPageViewProps) {
  if (reveal.status !== "revealed") return null;
  const { details } = reveal;
  const time = formatTime(details.revealedAt);
  return (
    <div className="banner ok revealed" role="status">
      <span className="revealed-details">
        <span>
          {t("customers.reveal.phone")}{" "}
          <b className="mono" data-contact="revealed">
            {details.unmaskedPhone ?? t("customers.reveal.none")}
          </b>
        </span>
        <span>
          {t("customers.reveal.email")}{" "}
          <b className="mono" data-contact="revealed">
            {details.unmaskedEmail ?? t("customers.reveal.none")}
          </b>
        </span>
        <span className="meta">{t("customers.reveal.recorded", { time })}</span>
      </span>
      <button className="btn quiet small" type="button" onClick={() => onReveal({ type: "close" })}>
        {t("customers.reveal.hide")}
      </button>
    </div>
  );
}

function RevealDialog({ account, reveal, onReveal }: CustomerPageViewProps) {
  if (reveal.status !== "asking" && reveal.status !== "sending") return null;
  const sending = reveal.status === "sending";
  const problem = reveal.status === "asking" ? reveal.problem : null;
  const error = reveal.status === "asking" ? reveal.error : null;
  const close = () => onReveal({ type: "close" });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    onReveal({ type: "submit" });
  };
  return (
    <Modal title={t("customers.reveal.title", { name: nameOf(account.identity) })} onClose={close}>
      <form className="modal-form" onSubmit={submit} noValidate>
        <div className="modal-body">
          <p>{t("customers.reveal.body")}</p>
        </div>
        <div className="field">
          <label htmlFor="reveal-reason">{t("customers.reveal.reason")}</label>
          <textarea
            id="reveal-reason"
            rows={2}
            value={reveal.reason}
            placeholder={t("customers.reveal.placeholder")}
            maxLength={REVEAL_REASON_MAX_LENGTH}
            disabled={sending}
            aria-invalid={problem ? true : undefined}
            onChange={(event) => onReveal({ type: "reason", reason: event.target.value })}
          />
        </div>
        {problem ? <p className="field-problem">{problem}</p> : null}
        {error ? <RevealFailure error={error} /> : null}
        <div className="btns modal-actions">
          <button className="btn" type="button" onClick={close} disabled={sending}>
            {t("customers.reveal.cancel")}
          </button>
          <button className="btn primary" type="submit" disabled={sending}>
            {sending ? t("customers.reveal.sending") : t("customers.reveal.submit")}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** A reveal that got no answer may still have been recorded: say so, with the request ID. */
function RevealFailure({ error }: { error: unknown }) {
  if (error instanceof ConsoleApiError && error.outcomeUnknown) {
    const { reference } = describeError(error);
    return (
      <div className="error" role="alert">
        <span>{t("customers.reveal.noAnswer")}</span>
        <span className="ref">{reference}</span>
      </div>
    );
  }
  return <ErrorNotice error={error} />;
}

/** "Agir pour le compte de…", as in the prototype. Its server isn't built yet. */
function OnBehalfPanel({ name }: { name: string }) {
  return (
    <div className="obo" aria-disabled="true">
      <b>{t("customers.obo.title", { name })}</b>
      <span className="meta">{t("customers.obo.body")}</span>
      <div className="btns">
        <button className="btn primary" type="button" disabled title={t("customers.obo.soon")}>
          {t("customers.obo.send")}
        </button>
        <span className="pill mute">{t("customers.obo.soon")}</span>
      </div>
    </div>
  );
}

// ── Aperçu ────────────────────────────────────────────────────────────────

function Overview({ account, now }: { account: CustomerAccount; now?: Date }) {
  const pro = account.proProfile;
  const restrictions = describeRestrictions(account.settings, now);
  const identity = verification(account.identity.verificationStatus);
  return (
    <div className="grid3">
      <div className="card">
        <h2>{t("customers.pro.title")}</h2>
        {pro ? (
          <>
            <div>
              <b>{tradeName(pro.trade)}</b> · {pro.city}
            </div>
            <ul className="kv">
              <li>
                <span>{t("customers.pro.identity")}</span>
                <span className={`pill ${identity.tone}`}>{identity.label}</span>
              </li>
              <li>
                <span>{t("customers.pro.approval")}</span>
                <span className={`pill ${approval(pro.approvalStatus).tone}`}>
                  {approval(pro.approvalStatus).label}
                </span>
              </li>
              <li>
                <span>{t("customers.pro.rating")}</span>
                <span>{rating(pro)}</span>
              </li>
              <li>
                <span>{t("customers.pro.rate")}</span>
                <span>{rate(pro)}</span>
              </li>
              <li>
                <span>{t("customers.pro.subscription")}</span>
                <span>{subscription(pro, now)}</span>
              </li>
            </ul>
          </>
        ) : (
          <p className="foot">{t("customers.pro.none")}</p>
        )}
      </div>
      <div className="card">
        <h2>{t("customers.client.title")}</h2>
        <ul className="kv">
          <li>
            <span>{t("customers.client.jobsPosted")}</span>
            <span>{account.client.jobsPosted}</span>
          </li>
          <li>
            <span>{t("customers.client.lastActivity")}</span>
            <span>
              {account.client.lastActivityAt
                ? formatDay(account.client.lastActivityAt, now)
                : t("customers.client.never")}
            </span>
          </li>
        </ul>
      </div>
      <div className={`card restrictions ${account.restricted ? "on" : ""}`}>
        <h2>{t("customers.restrictions.title")}</h2>
        {account.restricted ? (
          <>
            <div>
              <span className="pill bad">{t("customers.restrictions.suspension")}</span>
            </div>
            {restrictions.map((restriction) => (
              <p className="foot" key={restriction.line}>
                {restriction.line}
              </p>
            ))}
          </>
        ) : (
          <p className="foot">{t("customers.restrictions.none")}</p>
        )}
      </div>
    </div>
  );
}

// ── Abonnement ───────────────────────────────────────────────────────────

/** Billing's view of this pro. A client has no pro profile, so nothing to read. */
function Subscription({ account, subscriptions }: CustomerPageViewProps) {
  const pro = account.proProfile;
  if (!pro) {
    return (
      <div className="card">
        <p className="foot">{t("customers.pro.none")}</p>
      </div>
    );
  }
  return <SubscriptionView workerId={pro.proProfileId} {...subscriptions} />;
}

// ── Fonctionnalités & réglages ───────────────────────────────────────────

function SettingsTable({ account, now }: { account: CustomerAccount; now?: Date }) {
  const groups = new Map<ResolvedCustomerSetting["category"], ResolvedCustomerSetting[]>();
  for (const setting of account.settings) {
    groups.set(setting.category, [...(groups.get(setting.category) ?? []), setting]);
  }
  return (
    <div className="card">
      {account.settings.length === 0 ? (
        <div className="empty">{t("customers.settings.none")}</div>
      ) : (
        <div className="table-wrap">
          <table className="customer-settings">
            <thead>
              <tr>
                <th>{t("customers.settings.col.setting")}</th>
                <th>{t("customers.settings.col.value")}</th>
                <th>{t("customers.settings.col.source")}</th>
                <th>{t("customers.settings.col.reason")}</th>
                <th>{t("customers.settings.col.expires")}</th>
              </tr>
            </thead>
            <tbody>
              {[...groups].map(([category, settings]) => (
                <Fragment key={category}>
                  <tr className="cat">
                    <td colSpan={5}>{categoryName(category)}</td>
                  </tr>
                  {settings.map((setting) => (
                    <SettingRow key={setting.settingId} setting={setting} account={account} now={now} />
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="foot">{t("customers.settings.foot")}</p>
      <div className="btns">
        <span title={t("customers.settings.editSoon")}>
          <button className="btn" type="button" disabled aria-describedby="settings-edit-soon">
            {t("customers.settings.edit")}
          </button>
        </span>
        <span className="meta" id="settings-edit-soon">
          {t("customers.settings.editSoon")}
        </span>
      </div>
    </div>
  );
}

function SettingRow({
  setting,
  account,
  now,
}: {
  setting: ResolvedCustomerSetting;
  account: CustomerAccount;
  now?: Date;
}) {
  const value = displayValue(setting);
  return (
    <tr className={setting.restricted ? "restricted" : undefined} data-setting={setting.settingId}>
      <td>
        {settingName(setting.settingId)}
        <br />
        <span className="mono meta">{setting.settingId}</span>
        {setting.restricted ? (
          <>
            {" "}
            <span className="pill bad">{t("customers.settings.restricted")}</span>
          </>
        ) : null}
      </td>
      <td>
        {value.kind === "text" ? value.text : <span className={`pill ${valueTone(setting, value)}`}>{value.text}</span>}
      </td>
      <td>{sourceLabel(setting.source, account.identity.region)}</td>
      <td>{setting.reason ? <bdi>{setting.reason}</bdi> : <span className="meta">—</span>}</td>
      <td>{setting.expiresAt ? formatDay(setting.expiresAt, now) : "—"}</td>
    </tr>
  );
}

// ── Demandes ─────────────────────────────────────────────────────────────

function Jobs({ account, now }: { account: CustomerAccount; now?: Date }) {
  if (account.recentJobs.length === 0) {
    return (
      <div className="card">
        <div className="empty">{t("customers.jobs.none")}</div>
      </div>
    );
  }
  return (
    <div className="card">
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>{t("customers.jobs.col.trade")}</th>
              <th>{t("customers.jobs.col.type")}</th>
              <th>{t("customers.jobs.col.side")}</th>
              <th>{t("customers.jobs.col.other")}</th>
              <th>{t("customers.jobs.col.status")}</th>
              <th>{t("customers.jobs.col.date")}</th>
            </tr>
          </thead>
          <tbody>
            {account.recentJobs.map((job) => {
              const status = jobStatus(job.status);
              return (
                <tr key={job.jobId}>
                  <td>{tradeName(job.trade)}</td>
                  <td>{t(`customers.jobs.type.${job.type}` as const)}</td>
                  <td>{t(`customers.jobs.side.${job.side}` as const)}</td>
                  <td>
                    {job.otherParty ? (
                      <a href={customerHref(job.otherParty.userId)}>{otherParty(job)}</a>
                    ) : (
                      otherParty(job)
                    )}
                  </td>
                  <td>
                    <span className={`pill ${status.tone}`}>{status.label}</span>
                  </td>
                  <td>{formatDay(job.createdAt, now)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="foot">{t("customers.jobs.foot", { count: ACCOUNT_RECENT_JOBS_LIMIT })}</p>
    </div>
  );
}

// ── Journal ──────────────────────────────────────────────────────────────

function Journal({ account, canViewAudit, journal, roles, onJournalRetry, onJournalMore, now }: CustomerPageViewProps) {
  if (!canViewAudit) {
    return (
      <FeatureLock
        lead={t("customers.journal.locked")}
        feature="VIEW_AUDIT_LOG_STAFF_FEATURE_ENABLEMENT"
        roles={roles}
      />
    );
  }
  return (
    <div className="card">
      <p className="foot">{t("customers.journal.recorded")}</p>
      {journal.status === "loading" ? (
        <div className="empty" role="status" aria-live="polite">
          <span className="spinner" aria-hidden="true" />
          <span>{t("audit.loading")}</span>
        </div>
      ) : journal.status === "error" ? (
        <div className="audit-error">
          <p className="foot">{t("audit.loadFailed")}</p>
          <ErrorNotice error={journal.error} />
          <div>
            <button className="btn primary" type="button" onClick={onJournalRetry}>
              {t("audit.retry")}
            </button>
          </div>
        </div>
      ) : journal.entries.length === 0 ? (
        <div className="empty">{t("customers.journal.empty")}</div>
      ) : (
        <div className="timeline">
          {journal.entries.map((entry) => (
            <JournalEvent key={entry.auditLogId} entry={entry} now={now} />
          ))}
        </div>
      )}
      {journal.status === "ready" ? (
        <div className="audit-more">
          {journal.moreError ? <ErrorNotice error={journal.moreError} /> : null}
          <div className="btns">
            {journal.nextCursor ? (
              <button className="btn" type="button" onClick={onJournalMore} disabled={journal.loadingMore}>
                {journal.loadingMore
                  ? t("audit.loadingMore")
                  : journal.moreError
                    ? t("audit.retry")
                    : t("audit.loadMore")}
              </button>
            ) : null}
            <a className="go-link" href={auditHash({ subjectUserId: account.identity.userId })}>
              {t("customers.journal.all")}
            </a>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function JournalEvent({ entry, now }: { entry: AuditLogEntry; now?: Date }) {
  const actor = describeActor(entry);
  const change = describeChange(entry);
  const when = `${formatDay(entry.occurredAt, now)} ${formatTime(entry.occurredAt)}`;
  const head = [
    when,
    entry.actorType === "staff" && entry.actorStaffRoles?.length
      ? t("customers.journal.actorWithRoles", { name: actor.name, roles: entry.actorStaffRoles.join(", ") })
      : actor.name,
    entry.onBehalfOfUserId ? t("customers.journal.onBehalf", { id: shortId(entry.onBehalfOfUserId) }) : null,
    entry.actionId,
  ]
    .filter((part): part is string => Boolean(part))
    .map(isolate);
  const note = [
    change.kind === "lines" ? change.lines.join(" · ") : change.kind === "hidden" ? t("audit.masked") : null,
    entry.reason ? t("customers.journal.reason", { reason: entry.reason }) : null,
    entry.outcome === "denied" ? t("customers.journal.denied") : t("customers.journal.succeeded"),
  ]
    .filter((part): part is string => Boolean(part))
    .map(isolate);
  return (
    <div
      className={["ev", entry.actorType === "staff" ? "staff" : "", entry.outcome === "denied" ? "refused" : ""]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="when">{head.join(" · ")}</div>
      <div className="note">{note.join(" · ")}</div>
    </div>
  );
}
